import React, { useState, useEffect, useMemo } from "react";
import "./AuditTrail.css";
import { Avatar, List, DatePicker, Button } from "antd";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../Firebase/FirebaseConnection";
import { generateAvatarSeed } from "./ManageUser";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

const { RangePicker } = DatePicker;
dayjs.extend(isBetween);

const AuditTrail = () => {
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "user-activity-logs"),
      (snapshot) => {
        setData(snapshot.docs.map((doc) => generateAuditEntry(doc.data())));
      }
    );

    return () => unsubscribe();
  }, []);

  const generateAuditEntry = (record) => {
    const actionMessages = {
      delete: [
        `Book Deleted Title: "${record.bookTitle}"`,
        `Deleted by: ${record.updatedBy} | Author: ${record.oldStatus}`,
      ],
      add: [
        `New Book Added: '${record.bookTitle}'`,
        `Added by: ${record.updatedBy} | Author: ${record.author} | ISBN: ${record.isbn}`,
      ],
      edit: [
        `${record.updatedBy} updated book details. Title: ${record.bookTitle}`,
        `Status changed from '${record.oldStatus}' to '${record.newStatus}'`,
      ],

      editUser: [
        `Student Book Status Edited: '${record.bookTitle}'`,
        `Edited by: ${record.updatedBy} | Old Status: ${record.oldStatus} | New Status: ${record.newStatus}`,
      ],
      editSession: (() => {
        const changesText = Object.entries(record.changes || {})
          .map(([field, value]) => `${field}: "${value.old}" ➡ "${value.new}"`)
          .join(" | ");

        return [
          `${record.updatedBy} Updated Student Details. Student Name: ${
            record.studentName || "Unknown"
          } `,
          `Changes: ${changesText || "No visible changes"} 
          }`,
        ];
      })(),
      statusUpdate: [
        `${record.updatedBy} Updated Book Status: '${record.bookTitle}'`,
        record.actionDescription,
      ],
      clearBookStatus: [
        `${record.updatedBy} Cleared book status: '${record.bookTitle}'`,
        record.actionDescription,
      ],
      editUser: [
        `${record.performedBy} updated staff details`,
        `Staff Edited: ${record.details}`,
      ],
      finePaid: [
        `${record.updatedBy} Marked book as Paid: '${record.bookTitle}'`,
        record.actionDescription,
      ],
      deleteUser: [`${record.performedBy} deleted a staff`, record.details],
      addUser: [`${record.performedBy} added new member`, record.details],
      issueBook: [`${record.performedBy} issued a book`, record.details],
      returnBook: [
        `${record.updatedBy} accepted a book return`,
        record.actionDescription,
      ],
    };

    const [title, description] = actionMessages[record.actionType] || [
      `Unknown Action on Book: '${record.bookTitle}'`,
      `Performed by: ${record.updatedBy}`,
    ];

    return {
      title,
      description,
      date: record.timestamp?.toDate ? record.timestamp.toDate() : null,
      avatarSeed: record.avatarSeed ?? generateAvatarSeed(record.updatedBy),
    };
  };

  const filteredData = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1])
      return data;

    const [start, end] = dateRange.map((date) => dayjs(date).startOf("day"));

    return data.filter(
      ({ date }) => date && dayjs(date).isBetween(start, end, "day", "[]")
    );
  }, [dateRange, data]);

  return (
    <div className="audit-trail-main-container">
      <div className="filter-container">
        <RangePicker
          onChange={(dates) => setDateRange(dates ? [dates[0], dates[1]] : [])}
        />
        <Button type="primary" onClick={() => setDateRange([...dateRange])}>
          Filter
        </Button>
      </div>
      <List
        itemLayout="horizontal"
        dataSource={filteredData}
        renderItem={({ title, description, date, avatarSeed }) => (
          <List.Item className="custom-list-item">
            <List.Item.Meta
              avatar={
                <Avatar
                  className="custom-avatar"
                  src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${avatarSeed}`}
                />
              }
              title={<span className="list-item-title">{title}</span>}
              description={
                <span className="list-item-description">
                  {description} | Date:{" "}
                  {date
                    ? dayjs(date).format("MMMM D, YYYY h:mm A")
                    : "Date Not Available"}
                </span>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export default AuditTrail;
